import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUp, Download, ListTree, PanelLeftClose, PanelLeftOpen, Ruler } from 'lucide-react'
import type { ClinSparkStudyPackage, TabKey } from '../../types'
import {
  anchorId,
  buildCriterionIndex,
  criterionKey,
  displayLabel,
  downloadText,
  humanizeCode,
  known,
  parseCriterionNote,
  relationKey,
  rows,
  sameKnown,
  scopeLabel,
  searched,
  stripTruncation,
  toCsv,
  value,
  type CriterionRecord,
  type Row,
} from '../../utils/parser'
import { usePersistentState } from '../../hooks/usePersistentState'
import { CriteriaReferences, isCriteriaDisplayMode, type CriteriaDisplayMode, type CriterionRef } from './Criteria'
import { Badge, CopyButton, Highlight, Metric, Notice, SearchInput, Section, SegmentedControl } from './StudyUi'

type RangeRef = { range_id?: string; item_name?: string; display?: string; stated_display?: string; range_type?: string; criterion_ref?: string | null; qualifier?: string | null }

type Entry = {
  form: Row | null
  procedures: Row[]
  criteria: CriterionRef[]
  ranges: RangeRef[]
  notes: string[]
  fieldCount: number
}

type EntryStatus = 'mapping-needed' | 'field-design' | 'field-ready'
type StatusFilter = 'all' | EntryStatus

export type BlueprintVisit = Row & { entries: Entry[]; navKey: string; status: EntryStatus | 'empty' }

export type NavigateFn = (tab: TabKey, anchor?: string) => void

/** A request to scroll to (and briefly highlight) an element in a tab. */
export type FocusRequest = { anchor: string; seq: number }

const noteLines = (item: unknown): string[] => {
  if (!known(item)) return []
  return String(item).split(/\r?\n/).map((line) => line.trim()).filter((line) => known(line) && !/^[xX][a-z]?$/.test(line))
}

/** Visit title for display: the label without the schedule-scope prefix
 * that keeps visit_name unique across study parts. */
const visitTitle = (visit: Row): string => displayLabel(known(visit.visit_label) ? visit.visit_label : visit.visit_name)

const timingLabel = (item: Row): string => {
  const timing = item.timepoint ?? item.schedule_position
  if (!known(timing)) return ''
  const visit = item.visit_name ?? item.visit
  // A schedule visit's timepoint repeats its own label; a nested timepoint
  // row's label is the timing point itself and must stay visible.
  if (sameKnown(timing, visit) || (!known(item.parent_visit_id) && sameKnown(timing, item.visit_label))) return ''
  if (sameKnown(timing, item.study_day ?? item.scheduled_day)) return ''
  return value(timing)
}

function entryStatus(entry: Entry): EntryStatus {
  if (!entry.form) return 'mapping-needed'
  return entry.fieldCount ? 'field-ready' : 'field-design'
}

function visitStatus(entries: Entry[]): EntryStatus | 'empty' {
  if (entries.every((entry) => !entry.form && !entry.procedures.length)) return 'empty'
  if (entries.some((entry) => entryStatus(entry) === 'mapping-needed')) return 'mapping-needed'
  if (entries.some((entry) => entryStatus(entry) === 'field-design')) return 'field-design'
  return 'field-ready'
}

/** Split form/procedure notes into criterion references, range references and plain notes. */
function collectAnnotations(sources: { notes: unknown[]; eligibility: Row[]; ranges: Row[] }, index: Map<string, CriterionRecord>) {
  const criteria = new Map<string, CriterionRef>()
  const notes: string[] = []
  const addCriterion = (prefix: 'INC' | 'EXC', number: string, noteText: string) => {
    const key = criterionKey(prefix, number)
    if (criteria.has(key)) return
    const record = index.get(key)
    criteria.set(key, record
      ? { key, prefix, number: record.number, text: record.formatted || record.text, population: record.population, resolved: true }
      : { key, prefix, number, text: stripTruncation(noteText), resolved: false })
  }
  for (const line of sources.notes.flatMap(noteLines)) {
    const parsed = parseCriterionNote(line)
    if (parsed) addCriterion(parsed.prefix, parsed.number, parsed.text)
    else if (!notes.includes(line)) notes.push(line)
  }
  for (const reference of sources.eligibility) {
    const display = known(reference.display) ? parseCriterionNote(String(reference.display)) : null
    if (display) {
      addCriterion(display.prefix, display.number, display.text)
      continue
    }
    const prefix = String(reference.criterion_type || '').toLowerCase().startsWith('ex') ? 'EXC' : 'INC'
    const number = value(reference.criterion_number ?? reference.number ?? reference.id)
    if (number !== '-') addCriterion(prefix, number, String(reference.criterion_text || reference.text || ''))
  }
  const ranges: RangeRef[] = []
  for (const range of sources.ranges) {
    if (!ranges.some((existing) => existing.range_id === range.range_id)) ranges.push(range as RangeRef)
  }
  const order = (ref: CriterionRef) => [ref.prefix === 'INC' ? 0 : 1, Number.parseFloat(ref.number) || 0] as const
  const sortedCriteria = [...criteria.values()].sort((a, b) => order(a)[0] - order(b)[0] || order(a)[1] - order(b)[1])
  return { criteria: sortedCriteria, ranges, notes }
}

/** Derives the visit -> form -> procedure build map from the CMM graph. */
export function useBlueprintModel(data: ClinSparkStudyPackage) {
  return useMemo(() => buildBlueprintModel(data), [data])
}

export function buildBlueprintModel(data: ClinSparkStudyPackage) {
  const cmm = (data.study_build_design?.CMM || {}) as Row
  // The CMM dictionaries are the canonical graph for the blueprint. The
  // package-level arrays are useful fallbacks, but can have a different
  // occurrence shape after timepoint expansion.
  const visits = rows(cmm.visit_dictionary || data.visits)
  const procedures = rows(cmm.procedure_dictionary || data.procedures)
  const forms = rows(cmm.form_dictionary || data.forms)
  const fields = rows(cmm.field_dictionary)
  const mappings = rows(cmm.procedure_form_mapping)
  const criterionIndex = buildCriterionIndex(data.eligibility)

  const visitNameCounts = new Map<string, number>()
  for (const visit of visits) {
    const key = relationKey(visit.visit_name)
    visitNameCounts.set(key, (visitNameCounts.get(key) || 0) + 1)
  }
  const formById = new Map(forms.map((form) => [String(form.form_id), form]))
  const formsByProcedure = new Map<string, Row[]>()
  for (const mapping of mappings) {
    const list = formsByProcedure.get(mapping.procedure_id) || []
    const form = formById.get(String(mapping.form_id))
    if (form && !list.includes(form)) list.push(form)
    formsByProcedure.set(mapping.procedure_id, list)
  }
  const fieldsByForm = new Map<string, Row[]>()
  for (const field of fields) fieldsByForm.set(field.form_id, [...(fieldsByForm.get(field.form_id) || []), field])
  const approvedFieldsByForm = new Map<string, Row[]>()
  for (const field of fields.filter((item) => !item.human_review_required && item.status !== 'HUMAN_REVIEW_REQUIRED')) {
    approvedFieldsByForm.set(field.form_id, [...(approvedFieldsByForm.get(field.form_id) || []), field])
  }

  const visitRows: Row[] = visits.map((visit) => {
    const explicitProcedureIds = new Set(
      (Array.isArray(visit.procedure_ids) ? visit.procedure_ids : []).map((item: unknown) => (
        item && typeof item === 'object'
          ? String((item as Row).procedure_id || (item as Row).id || '')
          : String(item)
      )).filter(Boolean)
    )
    const visitProcedures = procedures.filter((procedure) => {
      if (explicitProcedureIds.has(String(procedure.procedure_id))) return true
      if (known(procedure.visit_id)) return relationKey(procedure.visit_id) === relationKey(visit.visit_id)
      const name = relationKey(procedure.visit || procedure.visit_name)
      return name === relationKey(visit.visit_name) && visitNameCounts.get(name) === 1
    })
    const visitForms = visitProcedures.flatMap((procedure) => formsByProcedure.get(procedure.procedure_id) || []).filter((form, index, list) => list.indexOf(form) === index)
    return {
      ...visit,
      scheduled_day: visit.scheduled_day ?? visit.study_day,
      timepoint: visit.timepoint ?? visit.schedule_position,
      procedures: visitProcedures,
      forms: visitForms,
    }
  })

  const visitById = new Map(visits.map((visit) => [String(visit.visit_id), visit]))
  // A schedule visit expanded by an hourly table is itself a row and the
  // parent of its timepoint rows; key both by the parent id so they share
  // one card.
  const parentIds = new Set(visits.filter((visit) => known(visit.parent_visit_id)).map((visit) => String(visit.parent_visit_id)))
  const grouped = new Map<string, Row>()
  for (const visit of visitRows) {
    const parentVisit = known(visit.parent_visit_id) ? visitById.get(String(visit.parent_visit_id)) : null
    const parentName = known(visit.parent_visit_name) ? visit.parent_visit_name : parentVisit?.visit_name || visit.visit_name
    const groupKey = known(visit.parent_visit_id)
      ? `${value(visit.period)}|${value(visit.parent_visit_id)}`
      : parentIds.has(String(visit.visit_id))
        ? `${value(visit.period)}|${value(visit.visit_id)}`
        : `${value(visit.period)}|${value(parentName)}|${value(visit.scheduled_day)}`
    const existing = grouped.get(groupKey)
    const base = existing || {
      ...visit,
      visit_id: known(visit.parent_visit_id) ? visit.parent_visit_id : visit.visit_id,
      visit_name: parentName,
      visit_label: parentVisit ? parentVisit.visit_label : known(visit.parent_visit_id) ? undefined : visit.visit_label,
      scheduled_day: parentVisit?.study_day ?? parentVisit?.scheduled_day ?? visit.scheduled_day,
      timepoints: [],
      procedures: [],
      forms: [],
      groupKey,
    }
    base.timepoints = [...(base.timepoints || []), visit].filter((item: Row, index: number, list: Row[]) => list.findIndex((candidate) => candidate.visit_id === item.visit_id) === index)
    base.procedures = [...(base.procedures || []), ...visit.procedures].filter((item: Row, index: number, list: Row[]) => list.findIndex((candidate) => candidate.procedure_id === item.procedure_id) === index)
    base.forms = [...(base.forms || []), ...visit.forms].filter((item: Row, index: number, list: Row[]) => list.findIndex((candidate) => candidate.form_id === item.form_id) === index)
    // Procedure rows carry no visit name, so drop timings that merely
    // repeat the card's own visit name or label.
    base.timingLabels = [...new Set([
      ...(base.timepoints || []).map((item: Row) => timingLabel(item)).filter(Boolean),
      ...(base.procedures || []).map((item: Row) => timingLabel(item)).filter(Boolean),
    ])].filter((label) => !sameKnown(label, base.visit_name) && !sameKnown(label, base.visit_label))
    grouped.set(groupKey, base)
  }

  const usedKeys = new Set<string>()
  const blueprint: BlueprintVisit[] = [...grouped.values()].map((visit) => {
    const groupedEntries = new Map<string, { form: Row | null; procedures: Row[] }>()
    for (const procedure of visit.procedures as Row[]) {
      const linkedForms = formsByProcedure.get(procedure.procedure_id) || []
      if (!linkedForms.length) {
        groupedEntries.set(`unmapped:${procedure.procedure_id}`, { form: null, procedures: [procedure] })
        continue
      }
      for (const form of linkedForms) {
        const key = String(form.form_id)
        const entry = groupedEntries.get(key) || { form, procedures: [] }
        entry.procedures.push(procedure)
        groupedEntries.set(key, entry)
      }
    }
    const entries: Entry[] = [...groupedEntries.values()].map(({ form, procedures: linked }) => {
      const annotations = collectAnnotations({
        notes: [form?.notes, ...linked.map((procedure) => procedure.notes)],
        eligibility: rows(form?.eligibility_references),
        ranges: rows(form?.range_references),
      }, criterionIndex)
      return { form, procedures: linked, ...annotations, fieldCount: form ? approvedFieldsByForm.get(form.form_id)?.length || 0 : 0 }
    })
    if (!entries.length) entries.push({ form: null, procedures: [], criteria: [], ranges: [], notes: [], fieldCount: 0 })
    let navKey = anchorId('bp', visit.groupKey) || anchorId('bp', visit.visit_id)
    while (usedKeys.has(navKey)) navKey = `${navKey}-x`
    usedKeys.add(navKey)
    return {
      ...visit,
      entries,
      navKey,
      status: visitStatus(entries),
      fieldCount: (visit.forms as Row[]).reduce((total: number, form: Row) => total + (approvedFieldsByForm.get(form.form_id)?.length || 0), 0),
    } as BlueprintVisit
  })
  const mappedProcedureIds = new Set(blueprint.flatMap((visit) => (visit.procedures as Row[]).map((procedure) => String(procedure.procedure_id))))
  const unassignedProcedures = procedures.filter((procedure) => !mappedProcedureIds.has(String(procedure.procedure_id)))
  return { visits, procedures, forms, fieldsByForm, approvedFieldsByForm, blueprint, unassignedProcedures }
}

const STATUS_OPTIONS = [
  ['all', 'All rows'],
  ['mapping-needed', 'Needs mapping'],
  ['field-design', 'Needs field design'],
  ['field-ready', 'Field-ready'],
] as const

const STATUS_DOT: Record<EntryStatus | 'empty', string> = {
  'field-ready': 'bg-emerald-500',
  'field-design': 'bg-amber-400',
  'mapping-needed': 'bg-rose-500',
  empty: 'bg-gray-300 dark:bg-gray-600',
}

const STATUS_TEXT: Record<EntryStatus | 'empty', string> = {
  'field-ready': 'All forms field-ready',
  'field-design': 'Field design needed',
  'mapping-needed': 'Has unmapped procedures',
  empty: 'No procedures mapped',
}

const isStatusFilter = (input: unknown): input is StatusFilter => STATUS_OPTIONS.some(([key]) => key === input)

export function BuildBlueprint({ data, search, onSearchChange, onNavigate }: {
  data: ClinSparkStudyPackage
  search: string
  onSearchChange?: (next: string) => void
  onNavigate?: NavigateFn
}) {
  const model = useBlueprintModel(data)
  const [formFilter, setFormFilter] = useState<'all' | 'field-ready' | 'needs-field-design'>('all')
  const [criteriaMode, setCriteriaMode] = usePersistentState<CriteriaDisplayMode>('pc.blueprint.criteriaMode', 'compact', isCriteriaDisplayMode)
  const [statusFilter, setStatusFilter] = usePersistentState<StatusFilter>('pc.blueprint.statusFilter', 'all', isStatusFilter)
  const [navOpen, setNavOpen] = usePersistentState<boolean>('pc.blueprint.navigator', true, (v): v is boolean => typeof v === 'boolean')
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [flashKey, setFlashKey] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const headRef = useRef<HTMLTableSectionElement>(null)

  const visible = useMemo(() => model.blueprint
    .filter((visit) => searched(visit, search))
    .map((visit) => ({ ...visit, entries: statusFilter === 'all' ? visit.entries : visit.entries.filter((entry) => entryStatus(entry) === statusFilter) }))
    .filter((visit) => visit.entries.length > 0), [model.blueprint, search, statusFilter])

  const unassigned = search.trim() ? [] : model.unassignedProcedures
  const fieldReadyCount = [...model.approvedFieldsByForm.values()].reduce((total, items) => total + items.length, 0)
  const statusCounts = useMemo(() => {
    const counts: Record<EntryStatus, number> = { 'mapping-needed': 0, 'field-design': 0, 'field-ready': 0 }
    for (const visit of model.blueprint) for (const entry of visit.entries) if (entry.form || entry.procedures.length) counts[entryStatus(entry)] += 1
    return counts
  }, [model.blueprint])

  const openCriterion = onNavigate ? (key: string) => onNavigate('ie', anchorId('ie', key)) : undefined
  const openRange = onNavigate ? (rangeId: string) => onNavigate('ranges', anchorId('range', rangeId)) : undefined

  const jumpTo = useCallback((navKey: string) => {
    const container = scrollRef.current
    const row = container?.querySelector<HTMLElement>(`[data-visit-key="${navKey}"]`)
    if (!container || !row) return
    if (typeof container.scrollIntoView === 'function') container.scrollIntoView({ block: 'nearest' })
    const offset = (headRef.current?.offsetHeight || 0) + 4
    if (typeof container.scrollTo === 'function') container.scrollTo({ top: Math.max(0, row.offsetTop - offset), behavior: 'smooth' })
    setActiveKey(navKey)
    setFlashKey(navKey)
  }, [])

  useEffect(() => {
    if (!flashKey) return
    const id = window.setTimeout(() => setFlashKey(null), 1600)
    return () => window.clearTimeout(id)
  }, [flashKey])

  // Track the visit at the top of the scroll area (no layout APIs needed beyond rects).
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    let frame = 0
    const update = () => {
      frame = 0
      const top = container.getBoundingClientRect().top + (headRef.current?.offsetHeight || 0) + 8
      let current: string | null = null
      for (const row of container.querySelectorAll<HTMLElement>('[data-visit-key]')) {
        if (row.getBoundingClientRect().top <= top) current = row.dataset.visitKey || null
        else break
      }
      setActiveKey(current ?? visible[0]?.navKey ?? null)
    }
    const schedule = typeof window.requestAnimationFrame === 'function'
      ? (cb: () => void) => window.requestAnimationFrame(cb)
      : (cb: () => void) => window.setTimeout(cb, 16)
    const cancel = typeof window.cancelAnimationFrame === 'function'
      ? (id: number) => window.cancelAnimationFrame(id)
      : (id: number) => window.clearTimeout(id)
    const onScroll = () => { if (!frame) frame = schedule(update) }
    update()
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', onScroll)
      if (frame) cancel(frame)
    }
  }, [visible])

  // J / K step through visits when focus is not in a text field.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return
      if (event.key !== 'j' && event.key !== 'k') return
      const index = visible.findIndex((visit) => visit.navKey === activeKey)
      const next = visible[Math.min(visible.length - 1, Math.max(0, (index < 0 ? 0 : index) + (event.key === 'j' ? 1 : -1)))]
      if (next) {
        event.preventDefault()
        jumpTo(next.navKey)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [visible, activeKey, jumpTo])

  const exportCsv = () => {
    const header = ['Period', 'Visit', 'Scheduled day', 'Window', 'Form', 'Procedures', 'Eligibility criteria', 'Ranges', 'Notes', 'Field-ready items', 'Build status']
    const body = model.blueprint.flatMap((visit) => visit.entries.map((entry) => [
      known(visit.period) ? visit.period : '',
      visit.visit_name,
      known(visit.scheduled_day) ? visit.scheduled_day : '',
      known(visit.visit_window) ? visit.visit_window : '',
      entry.form ? entry.form.form_name : '',
      entry.procedures.map((procedure) => [timingLabel(procedure), procedure.procedure_name].filter(Boolean).join(' - ')).join('; '),
      entry.criteria.map((ref) => `${ref.prefix} #${ref.number}`).join('; '),
      entry.ranges.map((range) => `${range.item_name}: ${range.display}`).join('; '),
      entry.notes.join(' | '),
      entry.fieldCount,
      STATUS_LABEL[entryStatus(entry)],
    ]))
    downloadText('clinspark_blueprint.csv', toCsv(header, body), 'text/csv;charset=utf-8')
  }

  return (
    <Section
      title="ClinSpark Build Blueprint"
      description="Visit → form → required procedures, with the criteria, ranges and notes each form must carry. Derived only from protocol evidence; unresolved design decisions are flagged rather than guessed."
      actions={<>
        <CopyButton text={() => model.blueprint.map((visit) => visit.visit_name).join('\n')} label="Copy visit list" />
        <button type="button" className="btn-secondary" onClick={exportCsv}><Download className="mr-1.5 h-4 w-4" aria-hidden={true} />Export CSV</button>
      </>}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Visits" value={model.blueprint.length} hint={`${model.visits.length} schedule columns`} />
        <Metric label="Forms" value={model.forms.length} />
        <Metric label="Procedures" value={model.procedures.length} />
        <Metric label="Field-ready items" value={fieldReadyCount} />
      </div>
      <SourceQuality data={data} />

      <div className="flex flex-wrap items-center gap-2">
        {onSearchChange && <SearchInput value={search} onChange={onSearchChange} placeholder="Search visits, forms, procedures, notes" className="w-full sm:w-80" />}
        <SegmentedControl label="Row status filter" options={STATUS_OPTIONS.map(([key, label]) => [key, key === 'all' ? label : `${label} (${statusCounts[key as EntryStatus]})`] as const)} valueKey={statusFilter} onChange={setStatusFilter} />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">I/E references</span>
          <SegmentedControl label="Eligibility criteria display" options={[['compact', 'Compact'], ['full', 'Full text']] as const} valueKey={criteriaMode} onChange={setCriteriaMode} />
        </div>
      </div>

      <div className={`grid gap-4 ${navOpen ? 'lg:grid-cols-[15rem_minmax(0,1fr)]' : ''}`}>
        {navOpen && (
          <VisitNavigator visits={visible} activeKey={activeKey} onJump={jumpTo} onClose={() => setNavOpen(false)} />
        )}
        <div className="min-w-0 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <div className="flex items-center gap-2">
              {!navOpen && (
                <button type="button" onClick={() => setNavOpen(true)} className="inline-flex items-center gap-1 rounded px-1.5 py-1 font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">
                  <PanelLeftOpen className="h-4 w-4" aria-hidden={true} />Visits
                </button>
              )}
              <span>{visible.length === model.blueprint.length ? `${visible.length} visits` : `${visible.length} of ${model.blueprint.length} visits`}</span>
            </div>
            <button type="button" onClick={() => { const c = scrollRef.current; if (c && typeof c.scrollTo === 'function') c.scrollTo({ top: 0, behavior: 'smooth' }) }} className="inline-flex items-center gap-1 rounded px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-800">
              <ArrowUp className="h-3.5 w-3.5" aria-hidden={true} />Top
            </button>
          </div>
          <div ref={scrollRef} className="relative max-h-[calc(100vh-11rem)] min-h-[20rem] overflow-auto">
            <table className="w-full min-w-[880px] border-separate border-spacing-0">
              <thead ref={headRef} className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Period / Visit', 'Forms / Required Procedures', 'Comments / Notes', 'Field-ready Items', 'Build Status'].map((column) => (
                    <th key={column} className="border-b border-gray-200 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-300">{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((visit) => visit.entries.map((entry, index) => (
                  <BlueprintRow
                    key={`${visit.navKey}-${entry.form?.form_id || entry.procedures[0]?.procedure_id || index}`}
                    visit={visit}
                    entry={entry}
                    first={index === 0}
                    flash={flashKey === visit.navKey}
                    criteriaMode={criteriaMode}
                    search={search}
                    onOpenCriterion={openCriterion}
                    onOpenRange={openRange}
                  />
                )))}
              </tbody>
            </table>
            {visible.length === 0 && (
              <p className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                {model.blueprint.length ? 'No visits match the current search or filter.' : 'No visits were extracted from the Schedule of Activities.'}
              </p>
            )}
          </div>
        </div>
      </div>

      {unassigned.length > 0 && <Notice>{`${unassigned.length} procedures have no unambiguous visit mapping: ${unassigned.map((procedure) => displayLabel(procedure.procedure_name)).join(', ')}`}</Notice>}

      {model.forms.length > 0 && (
        <details className="group space-y-3 pt-2" open={model.forms.length <= 12}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Form build definitions <span className="font-normal text-gray-500">({model.forms.length})</span></h4>
          </summary>
          <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
            <SegmentedControl label="Form readiness filter" options={[['all', 'All'], ['field-ready', 'Field-ready'], ['needs-field-design', 'Needs field design']] as const} valueKey={formFilter} onChange={setFormFilter} />
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {model.forms
              .filter((form) => searched(form, search) && (formFilter === 'all' || (formFilter === 'field-ready' ? Boolean(model.approvedFieldsByForm.get(form.form_id)?.length) : !model.approvedFieldsByForm.get(form.form_id)?.length)))
              .map((form) => {
                const ready = Boolean(model.approvedFieldsByForm.get(form.form_id)?.length)
                return (
                  <div key={form.form_id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white">{value(form.form_name)}</h5>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Domain: {value(form.domain)} | Repeatable: {value(form.repeatable)}</p>
                      </div>
                      <Badge tone={ready ? 'green' : 'amber'}>{ready ? 'Field-ready' : 'Needs field design'}</Badge>
                    </div>
                    <dl className="mt-3 grid grid-cols-1 gap-2 text-sm">
                      <div><dt className="text-xs text-gray-500">Source procedures</dt><dd className="text-gray-800 dark:text-gray-200">{value(form.source_procedure_ids || form.source_procedure)}</dd></div>
                      <div><dt className="text-xs text-gray-500">Scheduled visits</dt><dd className="text-gray-800 dark:text-gray-200">{value(form.source_visit_ids || form.scheduling)}</dd></div>
                      <div><dt className="text-xs text-gray-500">Field candidates</dt><dd className="text-gray-800 dark:text-gray-200">{model.fieldsByForm.get(form.form_id)?.map((field) => `${value(field.field_label || field.field_name)} (${value(field.field_type)})${field.human_review_required ? ' - review' : ''}`).join(', ') || 'No item-level fields in parser output'}</dd></div>
                    </dl>
                  </div>
                )
              })}
          </div>
        </details>
      )}
    </Section>
  )
}

const STATUS_LABEL: Record<EntryStatus, string> = {
  'mapping-needed': 'Mapping needed',
  'field-design': 'Field design needed',
  'field-ready': 'Field-ready',
}

function BlueprintRow({ visit, entry, first, flash, criteriaMode, search, onOpenCriterion, onOpenRange }: {
  visit: BlueprintVisit
  entry: Entry
  first: boolean
  flash: boolean
  criteriaMode: CriteriaDisplayMode
  search: string
  onOpenCriterion?: (key: string) => void
  onOpenRange?: (rangeId: string) => void
}) {
  const cell = `border-b border-gray-200 px-4 py-3 align-top dark:border-gray-700 ${flash ? 'bg-primary-50 dark:bg-primary-950/40' : ''}`
  const sameAsForm = (procedure: Row) => Boolean(entry.form) && relationKey(procedure.procedure_name) === relationKey(entry.form?.form_name)
  const status = entryStatus(entry)
  return (
    <tr className="group hover:bg-gray-50/70 dark:hover:bg-gray-800/40" data-visit-key={first ? visit.navKey : undefined} id={first ? visit.navKey : undefined}>
      {first && (
        <td rowSpan={visit.entries.length} className={`${cell} w-56 min-w-48 border-r bg-gray-50/60 text-sm dark:bg-gray-800/30`}>
          <div className="font-semibold text-gray-900 dark:text-white"><Highlight text={visitTitle(visit)} query={search} /></div>
          {known(visit.period) && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{value(visit.period)}</div>}
          {known(visit.scheduled_day) && <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">Scheduled: {value(visit.scheduled_day)}</div>}
          {visit.timingLabels?.length > 0 && <div className="text-xs text-gray-600 dark:text-gray-300">Timing points: {visit.timingLabels.join(', ')}</div>}
          {known(visit.visit_window) && <div className="text-xs text-gray-600 dark:text-gray-300">Window: {value(visit.visit_window)}</div>}
        </td>
      )}
      <td className={`${cell} min-w-64 text-sm text-gray-900 dark:text-white`}>
        <div className="font-medium"><Highlight text={entry.form ? displayLabel(entry.form.form_name) : entry.procedures.length ? 'Form mapping needed' : 'No procedures mapped'} query={search} /></div>
        {entry.procedures.filter((procedure) => !sameAsForm(procedure)).map((procedure) => (
          <div key={procedure.procedure_id} className="mt-1 text-xs text-gray-600 dark:text-gray-300">
            {timingLabel(procedure) && <span className="font-medium text-gray-800 dark:text-gray-100">{timingLabel(procedure)} - </span>}
            <Highlight text={displayLabel(procedure.procedure_name)} query={search} />{procedure.conditional ? ' (conditional)' : procedure.required ? ' (required)' : ''}
          </div>
        ))}
        {entry.procedures.filter(sameAsForm).some((procedure) => timingLabel(procedure)) && (
          <div className="mt-1 space-y-1 text-xs text-gray-600 dark:text-gray-300">
            {entry.procedures.filter(sameAsForm).map((procedure) => (
              <div key={procedure.procedure_id}><span className="font-medium text-gray-800 dark:text-gray-100">{timingLabel(procedure) || 'Scheduled'}</span>{procedure.conditional ? ' (conditional)' : procedure.required ? ' (required)' : ''}</div>
            ))}
          </div>
        )}
      </td>
      <td className={`${cell} min-w-72 max-w-xl break-words text-sm text-gray-700 dark:text-gray-200`}>
        {entry.criteria.length === 0 && entry.ranges.length === 0 && entry.notes.length === 0 && <span className="text-gray-400">-</span>}
        <div className="space-y-2">
          <CriteriaReferences refs={entry.criteria} mode={criteriaMode} search={search} onOpenCriterion={onOpenCriterion} />
          {entry.ranges.length > 0 && (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Protocol ranges">
              {entry.ranges.map((range) => {
                const label = `${range.item_name}: ${range.display}${range.qualifier ? ` (${range.qualifier})` : ''}`
                const content = <><Ruler className="h-3 w-3 shrink-0" aria-hidden={true} />{label}</>
                const className = 'inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-xs text-sky-800 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200'
                const title = `${range.range_type === 'eligibility' ? 'Acceptable range' : 'Alert threshold'}${range.criterion_ref ? ` · ${range.criterion_ref}` : ''}`
                return onOpenRange && range.range_id
                  ? <button key={range.range_id} type="button" title={`${title} — open in Ranges`} onClick={() => onOpenRange(String(range.range_id))} className={`${className} hover:bg-sky-100 dark:hover:bg-sky-900/50`}>{content}</button>
                  : <span key={range.range_id || label} title={title} className={className}>{content}</span>
              })}
            </div>
          )}
          {entry.notes.map((note, noteIndex) => <div key={`${note}-${noteIndex}`} className="whitespace-pre-wrap"><Highlight text={note} query={search} /></div>)}
        </div>
      </td>
      <td className={`${cell} text-sm text-gray-700 dark:text-gray-200`}>{entry.fieldCount || '-'}</td>
      <td className={`${cell} text-sm`}>
        <Badge tone={status === 'field-ready' ? 'green' : 'amber'}>{STATUS_LABEL[status]}</Badge>
      </td>
    </tr>
  )
}

function VisitNavigator({ visits, activeKey, onJump, onClose }: {
  visits: BlueprintVisit[]
  activeKey: string | null
  onJump: (navKey: string) => void
  onClose: () => void
}) {
  const [filter, setFilter] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const filtered = visits.filter((visit) => !filter.trim() || `${visit.visit_name} ${known(visit.visit_label) ? visit.visit_label : ''} ${value(visit.period)} ${value(visit.scheduled_day)}`.toLowerCase().includes(filter.trim().toLowerCase()))
  const groups: Array<{ period: string; visits: BlueprintVisit[] }> = []
  for (const visit of filtered) {
    const period = known(visit.period) ? String(visit.period) : 'Schedule'
    const last = groups[groups.length - 1]
    if (last && last.period === period) last.visits.push(visit)
    else groups.push({ period, visits: [visit] })
  }

  // Keep the active visit visible inside the navigator's own scroll area.
  useEffect(() => {
    const list = listRef.current
    const item = activeKey ? list?.querySelector<HTMLElement>(`[data-nav-key="${activeKey}"]`) : null
    if (!list || !item) return
    const itemTop = item.offsetTop
    if (itemTop < list.scrollTop || itemTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = Math.max(0, itemTop - list.clientHeight / 3)
    }
  }, [activeKey])

  return (
    <nav aria-label="Blueprint visits" className="flex max-h-[calc(100vh-11rem)] min-h-[20rem] flex-col rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:sticky lg:top-4">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white"><ListTree className="h-4 w-4 text-gray-500" aria-hidden={true} />Visits <span className="font-normal text-gray-500">{visits.length}</span></div>
        <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200" aria-label="Hide visit navigator" title="Hide visit navigator">
          <PanelLeftClose className="h-4 w-4" aria-hidden={true} />
        </button>
      </div>
      <div className="border-b border-gray-200 p-2 dark:border-gray-700">
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && filtered[0]) onJump(filtered[0].navKey)
            if (event.key === 'Escape') setFilter('')
          }}
          placeholder="Find a visit…"
          aria-label="Find a visit"
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        />
      </div>
      <div ref={listRef} className="relative min-h-0 flex-1 overflow-y-auto py-1">
        {groups.map((group, groupIndex) => (
          <div key={`${group.period}-${groupIndex}`}>
            {groups.length > 1 || group.period !== 'Schedule' ? (
              <div className="sticky top-0 z-[1] bg-white/95 px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wide text-gray-500 backdrop-blur dark:bg-gray-800/95 dark:text-gray-400">{group.period}</div>
            ) : null}
            <ul>
              {group.visits.map((visit) => {
                const active = visit.navKey === activeKey
                const formCount = visit.entries.filter((entry) => entry.form).length
                return (
                  <li key={visit.navKey}>
                    <button
                      type="button"
                      data-nav-key={visit.navKey}
                      onClick={() => onJump(visit.navKey)}
                      aria-current={active ? 'location' : undefined}
                      className={`flex w-full items-center gap-2 border-l-2 px-3 py-1.5 text-left text-sm transition-colors ${active ? 'border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-950/50 dark:text-primary-200' : 'border-transparent text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60'}`}
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[visit.status]}`} title={STATUS_TEXT[visit.status]} aria-hidden={true} />
                      <span className="min-w-0 flex-1 truncate" title={displayLabel(visit.visit_name)}>{visitTitle(visit)}</span>
                      <span className="shrink-0 text-2xs tabular-nums text-gray-400" title={`${formCount} forms`}>{formCount}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
        {filtered.length === 0 && <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">No visits match.</p>}
      </div>
      <div className="border-t border-gray-200 px-3 py-1.5 text-2xs text-gray-400 dark:border-gray-700">
        <kbd className="rounded border border-gray-200 px-1 dark:border-gray-600">J</kbd> <kbd className="rounded border border-gray-200 px-1 dark:border-gray-600">K</kbd> step through visits
      </div>
    </nav>
  )
}

export function SourceQuality({ data }: { data: ClinSparkStudyPackage }) {
  const quality = data.quality_control?.source_quality
  const scope = data.document_scope
  if (!quality && !scope) return null
  const findings = [...(quality?.blocking_issues || []), ...(quality?.warnings || [])]
  return (
    <section aria-label="Source quality" className="space-y-3 text-sm">
      {scope && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-3 text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
          <div className="flex flex-wrap items-center gap-3">
            <strong>Document scope</strong>
            <Badge tone={data.status === 'PARTIAL_SUCCESS' ? 'amber' : 'green'}>{scopeLabel(scope.protocol_scope || data.protocol_scope || 'FULL_PROTOCOL')}</Badge>
            {typeof scope.confidence === 'number' && <span className="text-xs">Confidence {(scope.confidence * 100).toFixed(0)}%</span>}
          </div>
          {scope.reason && <p className="mt-1">{scope.reason}</p>}
          {(scope.coverage || []).length > 0 && <p className="mt-1 text-xs">Coverage: {(scope.coverage || []).map(humanizeCode).join(', ')}</p>}
          {(scope.missing_expected_study_segments || []).length > 0 && <p className="mt-1 text-xs">Missing companion source: {(scope.missing_expected_study_segments || []).map(humanizeCode).join(', ')}</p>}
        </div>
      )}
      {quality && (
        <div className="flex flex-wrap items-center gap-4">
          <strong className="text-gray-900 dark:text-white">Source quality</strong>
          <Badge tone={quality.status === 'PASSED' ? 'green' : 'amber'}>{quality.status === 'PASSED' ? 'Automated checks passed' : quality.status === 'WARNINGS' ? 'Source warnings' : 'Review required before export'}</Badge>
          <span className="text-gray-600 dark:text-gray-300">{quality.selected_page_count} selected pages</span>
          {quality.ai_payload_token_count != null && <span className="text-gray-600 dark:text-gray-300">~{quality.ai_payload_token_count.toLocaleString()} prepared tokens</span>}
        </div>
      )}
      {findings.length > 0 && (
        <ul className="space-y-2">
          {findings.map((finding, index) => (
            <li key={`${finding.code}-${index}`} className="text-gray-700 dark:text-gray-200">
              <span className="font-medium">{finding.reason}</span>
              {finding.source_pages?.length > 0 && <span> (PDF p. {finding.source_pages.join(', ')})</span>}
              <div className="text-gray-500 dark:text-gray-400">{finding.recommended_action}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default React.memo(BuildBlueprint)
